"use client";

import { createContext, useCallback, useContext, useEffect, useState } from "react";

type ToastTone = "success" | "error" | "info";
type ToastItem = { id: number; message: string; tone: ToastTone };

type ToastContextValue = {
  /** 화면 우하단에 잠시 떠올랐다 사라지는 알림. 기본 톤은 성공. */
  toast: (message: string, tone?: ToastTone) => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

let seq = 0;

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);

  const dismiss = useCallback((id: number) => {
    setItems((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback((message: string, tone: ToastTone = "success") => {
    seq += 1;
    setItems((prev) => [...prev, { id: seq, message, tone }]);
  }, []);

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      <div className="pointer-events-none fixed bottom-6 right-6 z-[60] flex flex-col gap-2">
        {items.map((item) => (
          <ToastCard key={item.id} item={item} onDismiss={dismiss} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

/** 어느 콘솔 화면에서든 `toast(...)`로 완료/실패 알림을 띄운다. */
export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) return { toast: () => {} };
  return ctx;
}

const TONE: Record<ToastTone, { accent: string; soft: string; icon: React.ReactNode }> = {
  success: {
    accent: "var(--ok)",
    soft: "var(--ok-soft)",
    icon: (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <polyline points="20 6 9 17 4 12" />
      </svg>
    ),
  },
  error: {
    accent: "var(--danger)",
    soft: "var(--danger-soft)",
    icon: (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
      </svg>
    ),
  },
  info: {
    accent: "var(--primary)",
    soft: "var(--primary-soft)",
    icon: (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>
    ),
  },
};

function ToastCard({ item, onDismiss }: { item: ToastItem; onDismiss: (id: number) => void }) {
  const { id } = item;
  useEffect(() => {
    const timer = setTimeout(() => onDismiss(id), 3200);
    return () => clearTimeout(timer);
  }, [id, onDismiss]);

  const s = TONE[item.tone];
  return (
    <div
      role="status"
      className="pointer-events-auto flex items-center gap-2.5 rounded-[12px] bg-surface px-4 py-3"
      style={{ border: "1px solid var(--line)", boxShadow: "var(--shadow-lg)", animation: "toast-in 200ms ease-out" }}
    >
      <span
        className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full"
        style={{ background: s.soft, color: s.accent }}
      >
        {s.icon}
      </span>
      <p className="text-sm font-medium text-ink">{item.message}</p>
    </div>
  );
}
